import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useScrollLock } from "@/lib/scrollLock";
import type { LanguageCode } from "@/hooks/useRestaurant";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Subset of languages enabled on the restaurant settings doc. If null, show all. */
  languagesConfig: Partial<Record<LanguageCode, boolean>> | null;
}

interface Lang {
  code: LanguageCode;
  /** i18next code — PWA uses "en" instead of the tablet's "en-US". */
  i18nCode: string;
  flag: string;
  label: string;
}

const ALL_LANGS: Lang[] = [
  { code: "FR", i18nCode: "fr", flag: "🇫🇷", label: "Français" },
  { code: "EN", i18nCode: "en", flag: "🇬🇧", label: "English" },
  { code: "CN", i18nCode: "cn", flag: "🇨🇳", label: "中文" },
  { code: "IT", i18nCode: "it", flag: "🇮🇹", label: "Italiano" },
  { code: "ES", i18nCode: "es", flag: "🇪🇸", label: "Español" },
  { code: "DE", i18nCode: "de", flag: "🇩🇪", label: "Deutsch" },
  { code: "PT", i18nCode: "pt", flag: "🇵🇹", label: "Português" },
  { code: "JA", i18nCode: "ja", flag: "🇯🇵", label: "日本語" },
  { code: "KO", i18nCode: "ko", flag: "🇰🇷", label: "한국어" },
];

export const LanguagePicker = ({ open, onClose, languagesConfig }: Props): JSX.Element | null => {
  const { i18n, t } = useTranslation();
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  // If no config provided, show all. Otherwise filter.
  const visible = languagesConfig
    ? ALL_LANGS.filter((l) => languagesConfig[l.code])
    : ALL_LANGS;
  // If nothing matches (misconfigured restaurant), fall back to FR + EN.
  const display = visible.length > 0
    ? visible
    : ALL_LANGS.filter((l) => l.code === "FR" || l.code === "EN");

  const current = i18n.language;
  const onPick = (lang: Lang): void => {
    void i18n.changeLanguage(lang.i18nCode);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t("modalFlag.selectLanguage", "Choisissez votre langue")}
    >
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 animate-fade-in"
      />
      <div className="relative w-full sm:max-w-md bg-bg rounded-t-modal sm:rounded-modal max-h-[85vh] flex flex-col animate-slide-up shadow-warm-lg pb-safe">
        <header className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h2 className="display-font text-xl font-semibold text-ink">
            {t("modalFlag.selectLanguage", "Choisissez votre langue")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink"
            aria-label="Fermer"
          >
            ✕
          </button>
        </header>
        <ul className="flex-1 overflow-y-auto px-5 pb-5 grid grid-cols-2 gap-3">
          {display.map((lang) => {
            const active =
              current === lang.i18nCode ||
              // PWA uses "en", tablet "en-US" — treat both as English match.
              (lang.code === "EN" && (current === "en-US" || current === "en"));
            return (
              <li key={lang.code}>
                <button
                  type="button"
                  onClick={() => onPick(lang)}
                  className={`w-full flex items-center gap-3 p-3 rounded-button border-2 transition-colors ${
                    active
                      ? "border-primary bg-pink-softer"
                      : "border-border bg-surface hover:border-warm/60"
                  }`}
                >
                  <span className="text-2xl" aria-hidden="true">{lang.flag}</span>
                  <span className="text-sm font-medium text-ink truncate">{lang.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body
  );
};
